import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Camera, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import InstallAppButton from './InstallAppButton';

const Navbar = ({ variant = 'marketing' }) => {
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const nav = variant === 'marketing'
    ? [ { to: '/#features', label: 'Features' }, { to: '/#how', label: 'How it works' }, { to: '/pricing', label: 'Pricing' }, { to: '/#faq', label: 'FAQ' } ]
    : [ { to: '/app', label: 'Dashboard' }, { to: '/app/snap', label: 'Snap' }, { to: '/app/recipes', label: 'Recipes' }, { to: '/app/history', label: 'History' } ];

  const handleCta = () => {
    if (user) navigate('/app'); else setAuthOpen(true);
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#fefdf9]/80 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to={variant === 'marketing' ? '/' : '/app'} className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl bg-emerald-700 text-white grid place-items-center shadow-sm group-hover:rotate-6 transition-transform">
            <Camera className="w-5 h-5" strokeWidth={2.4} />
          </div>
          <span className="font-display text-xl font-bold text-emerald-950">CalorieSnap</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <a key={n.to} href={n.to} onClick={(e) => { if (n.to.startsWith('/#') && location.pathname === '/') { e.preventDefault(); document.querySelector(n.to.slice(1))?.scrollIntoView({ behavior: 'smooth' }); } }} className="text-sm font-medium text-emerald-950/70 hover:text-emerald-950 transition-colors">{n.label}</a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <InstallAppButton />
          {user ? (
            <div className="flex items-center gap-3">
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full ring-2 ring-emerald-100" />
              <span className="text-sm font-medium text-emerald-950">{user.name}</span>
              <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}><LogOut className="w-4 h-4" /></Button>
            </div>
          ) : (
            <>
              <Button variant="ghost" className="text-emerald-950 hover:bg-emerald-50" onClick={() => setAuthOpen(true)}>Sign in</Button>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-5" onClick={handleCta}>Start free trial</Button>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white">
          <div className="px-5 py-4 flex flex-col gap-3">
            {nav.map((n) => (<a key={n.to} href={n.to} className="py-2 text-emerald-950/80" onClick={() => setOpen(false)}>{n.label}</a>))}
            <div><InstallAppButton /></div>
            {user ? (
              <Button onClick={() => { logout(); navigate('/'); setOpen(false); }} variant="outline">Sign out</Button>
            ) : (
              <Button className="bg-emerald-700 text-white" onClick={() => { setAuthOpen(true); setOpen(false); }}>Start free trial</Button>
            )}
          </div>
        </div>
      )}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </header>
  );
};

export default Navbar;
